import { useId, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { WorkspaceDeleteError } from '../errors';
import { useDeleteWorkspace } from '../hooks/useDeleteWorkspace';

/**
 * Confirms a workspace delete before it happens. Permanent, and it takes every
 * workflow, task, and their history with it — said plainly, since none of it is
 * recoverable from here.
 *
 * Only owners and org admins reach it (spec 188, from Settings since spec 198);
 * typing the workspace's name is the deliberate act on top (spec FR-003). The
 * match is exact — the name is shown selectable so an unusual one can be
 * copied rather than reconstructed.
 *
 * Closing is blocked while the delete is in flight, so the mutation always
 * resolves in this dialog. Success closes it and tells the caller (`onDeleted`),
 * who has nothing left to stand on and leaves. "Wait and retry" (running work)
 * and "nothing happened, try again" (unreachable) stay inline for a retry.
 * "Already gone" is told by a toast, which outlives the page, and the caller
 * leaves as after a success.
 */
export function DeleteWorkspaceDialog({
  workspaceId,
  workspaceName,
  open,
  onOpenChange,
  onDeleted,
}: {
  workspaceId: string;
  workspaceName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The workspace is gone, by this delete or someone else's. */
  onDeleted?: () => void;
}) {
  const deleteMutation = useDeleteWorkspace();
  const { error, isPending } = deleteMutation;
  const [typedName, setTypedName] = useState('');
  const inputId = useId();

  const deleteError = error instanceof WorkspaceDeleteError ? error : null;
  const nameConfirmed = typedName === workspaceName;

  const closeAndReset = () => {
    onOpenChange(false);
    setTypedName('');
    deleteMutation.reset();
  };

  const handleOpenChange = (next: boolean) => {
    if (isPending) return;
    onOpenChange(next);
    if (!next) {
      setTypedName('');
      deleteMutation.reset();
    }
  };

  const confirmDelete = () => {
    if (isPending || !nameConfirmed) return;
    deleteMutation.mutate(workspaceId, {
      onSuccess: () => {
        closeAndReset();
        onDeleted?.();
      },
      onError: (mutationError) => {
        if (
          mutationError instanceof WorkspaceDeleteError &&
          mutationError.kind === 'not-found'
        ) {
          toast.info(mutationError.message);
          closeAndReset();
          onDeleted?.();
        }
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete {workspaceName}?</DialogTitle>
          <DialogDescription>
            This permanently deletes the workspace and everything in it — its
            workflows, its tasks, and their run history. Its usage and spend
            stay on the Usage pages under its name. This cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          <Label htmlFor={inputId} className="flex-wrap">
            Type{' '}
            <span className="text-foreground font-medium select-all">
              {workspaceName}
            </span>{' '}
            to confirm
          </Label>
          <Input
            id={inputId}
            value={typedName}
            onChange={(event) => {
              setTypedName(event.target.value);
            }}
            autoComplete="off"
            spellCheck={false}
            disabled={isPending}
          />
        </div>

        {deleteError !== null && (
          <p role="alert" className="text-destructive text-sm">
            {deleteError.message}
          </p>
        )}

        <DialogFooter>
          <DialogClose
            render={<Button variant="outline" disabled={isPending} />}
          >
            Cancel
          </DialogClose>
          <Button
            variant="destructive"
            disabled={isPending || !nameConfirmed}
            onClick={confirmDelete}
          >
            {isPending ? 'Deleting…' : 'Delete workspace'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
